// Shared "product shot" tilt: an element rests at a few degrees of
// perspective and eases flat as the cursor approaches it. Used by the hero
// panel, the feature showcases and the desktop-app mock so the whole page
// speaks one physical language. No-ops on touch / reduced motion.
import { useEffect, type RefObject } from 'react';

export function useProductTilt(
  ref: RefObject<HTMLElement | null>,
  restDeg: number,
  areaRef?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    const el = ref.current;
    const area = areaRef?.current ?? el;
    if (!el || !area) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    let raf = 0;
    let tx = 0, cx = 0; // 0 = resting tilt, 1 = flat
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      tx = Math.max(0, Math.min(1, 1.2 - d / (r.width * 0.85)));
    };
    const tick = () => {
      cx += (tx - cx) * 0.08;
      el.style.transform = `perspective(1600px) rotateY(${(restDeg * (1 - cx)).toFixed(2)}deg)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    area.addEventListener('pointermove', onMove);
    return () => {
      cancelAnimationFrame(raf);
      area.removeEventListener('pointermove', onMove);
      el.style.transform = '';
    };
  }, [ref, restDeg, areaRef]);
}
